import axios from "axios";
import config from "../config";
import { isDemoMode, demoDispatch, isDemoCredentials } from "./demoMode";
import { getSessionToken, clearSessionToken } from "./jwt-token-access/sessionToken";
import { GET_ME } from "./url_helper";

axios.defaults.baseURL = config.API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";
// DASH-024: send/receive the httpOnly auth cookie on API calls. Harmless before
// the API is same-site (no cookie yet → header path is used); required once it is.
axios.defaults.withCredentials = true;

// ── Demo-mode adapter ──────────────────────────────────────────────────────
// Short-circuits the network whenever the user is signed in via demo
// credentials (or is currently submitting them at /auth/login). Resolves
// to an AxiosResponse so the existing response interceptor still runs.
// axios 0.x exposed the default adapter as a function; 1.x exposes an ordered
// list of adapter NAMES (["xhr", "http", "fetch"]) that axios.getAdapter()
// resolves. Support both so this shim survives the major.
const realAdapter =
  typeof axios.defaults.adapter === "function"
    ? axios.defaults.adapter
    : axios.getAdapter(axios.defaults.adapter);
axios.defaults.adapter = async function (axiosConfig) {
  let isLoginRequest = false;
  let isDemoLoginAttempt = false;
  if (axiosConfig.url && axiosConfig.url.includes("/auth/login")) {
    isLoginRequest = true;
    if (axiosConfig.data) {
      try {
        const body = typeof axiosConfig.data === "string" ? JSON.parse(axiosConfig.data) : axiosConfig.data;
        if (body && isDemoCredentials(body.email, body.password)) {
          isDemoLoginAttempt = true;
        }
      } catch (_) { /* ignore */ }
    }
  }

  // Real-credential login bypasses demo even if a stale demo session exists.
  if (isLoginRequest && !isDemoLoginAttempt) {
    return realAdapter(axiosConfig);
  }
  if (!isDemoMode() && !isDemoLoginAttempt) {
    return realAdapter(axiosConfig);
  }

  try {
    const method = (axiosConfig.method || "GET").toUpperCase();
    const body = axiosConfig.data
      ? (typeof axiosConfig.data === "string" ? safeJSON(axiosConfig.data) : axiosConfig.data)
      : null;
    const data = demoDispatch(method, axiosConfig.url || "", body);
    return {
      data,
      status: 200,
      statusText: "OK (demo)",
      headers: {},
      config: axiosConfig,
      request: {},
    };
  } catch (err) {
    if (err && err.response) throw err;
    const wrapped = new Error(err && err.message ? err.message : "Demo error");
    wrapped.response = { status: 500, data: { message: wrapped.message } };
    throw wrapped;
  }
};
function safeJSON(s) { try { return JSON.parse(s); } catch (_) { return null; } }

// ── Request: auth via httpOnly cookie (sent automatically by withCredentials).
// DASH-024: we no longer read the token from localStorage. As a same-tab
// fallback we send the in-memory token as x-access-token if present, so a cookie
// hiccup can't lock the user out. (Legacy localStorage tokens from sessions
// created before this change are migrated to memory on bootstrap.)
axios.interceptors.request.use(
  (cfg) => {
    try {
      let t = getSessionToken();
      if (!t) {
        // Legacy fallback for a not-yet-migrated session (pre-DASH-024).
        const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
        if (authUser && authUser.accessToken) {
          t = authUser.accessToken.startsWith("Bearer ")
            ? authUser.accessToken.slice(7)
            : authUser.accessToken;
        }
      }
      if (t) {
        // axios 1.x headers are an AxiosHeaders instance (use .set); 0.x a plain object.
        if (cfg.headers && typeof cfg.headers.set === "function") cfg.headers.set("x-access-token", t);
        else cfg.headers["x-access-token"] = t;
      }
    } catch (_) { /* ignore */ }
    return cfg;
  },
  (err) => Promise.reject(err)
);

// Guards the /auth/me re-check below so a burst of 401s fires only one probe.
let revalidating = false;

// ── Response: unwrap body, route 401/403 sensibly ──────────────────────────
axios.interceptors.response.use(
  (response) => (response.data !== undefined ? response.data : response),
  (error) => {
    let message;
    const status = (error && error.response && error.response.status) || error.status;

    switch (status) {
      case 500:
        // Prefer the server's actual error message — the backend's errorResponse
        // sends a descriptive one, and masking it with a generic string makes
        // every 500 undiagnosable from the UI.
        message =
          (error.response && error.response.data && error.response.data.message) ||
          "Internal Server Error";
        break;
      case 401: {
        // Session invalid — clear and bounce to /login. BUT never bounce while
        // on a public auth page (accept-invite, reset/forgot password, login):
        // a 401 from the bootstrap /auth/me there is expected (no session yet),
        // and hard-redirecting would kick an invitee off the registration page.
        message = "Invalid credentials";
        const p = window.location.pathname || "";
        const onPublicAuthPage =
          p === "/login" ||
          p === "/forgot-password" ||
          p.startsWith("/accept-invite") ||
          p.startsWith("/reset-password");

        // Only /auth/me is authoritative about whether a session exists (see
        // store/auth/profile/saga.js). A 401 from any OTHER endpoint does not
        // prove the session is dead — it can be a stricter per-route permission
        // gate, a request that raced the cookie being set, or a transient
        // backend blip. Tearing the whole session down for one of those logs
        // the user out of a perfectly valid 90-day "Remember me" session, which
        // is exactly what an unexplained logout looks like from the outside.
        //
        // So: on a non-probe 401, re-ask /auth/me. Log out only if that also
        // 401s. `revalidating` prevents a stampede when several calls 401 at
        // once, and the probe is exempt from this branch so it cannot recurse.
        const url = String((error && error.config && error.config.url) || "");
        const isSessionProbe = url.includes(GET_ME);

        const endSession = () => {
          clearSessionToken();
          localStorage.removeItem("authUser");
          if (!onPublicAuthPage) window.location.href = "/login";
        };

        if (isSessionProbe) {
          endSession();
        } else if (!onPublicAuthPage && !revalidating) {
          revalidating = true;
          axios
            .get(GET_ME)
            .catch((e) => {
              if (e && e.response && e.response.status === 401) endSession();
            })
            .finally(() => {
              revalidating = false;
            });
        }
        break;
      }
      case 403:
        // Authenticated but lacking permission — DO NOT clear the session.
        // Calling pages render a friendly Forbidden / read-only state.
        message =
          (error.response && error.response.data && error.response.data.message) ||
          "You don't have permission to do that. Contact a super-admin.";
        break;
      case 404:
        message = "Sorry — the data you're looking for could not be found.";
        break;
      default:
        message =
          (error.response && error.response.data && error.response.data.message) ||
          (error && error.message) ||
          error;
    }
    // Reject with an Error that CARRIES the HTTP status. Rejecting a bare string
    // (the old behaviour) forced callers to regex-match English prose to tell
    // "the server says you have no session" apart from "the server was briefly
    // unreachable" — and getting that wrong turns a network blip into a logout.
    // `transient` marks failures that must NEVER be treated as a dead session:
    // no response at all (offline / DNS / CORS / aborted), 429, and any 5xx
    // (e.g. the edge 502s App Platform emits while a container is swapped).
    // Every existing consumer uses `typeof e === "string" ? e : e?.message`, so
    // they all keep reading the same text off `.message`.
    const err = new Error(typeof message === "string" ? message : String(message));
    err.status = status;
    err.transient = status === undefined || status === 429 || status >= 500;
    // The response body, for callers that map a stable error code (for example
    // `{ error: "already-listed" }`) to their own readable text.
    err.data = (error && error.response && error.response.data) || null;
    return Promise.reject(err);
  }
);

// ── Tiny axios facade used by every helper ─────────────────────────────────
export class APIClient {
  get(url, params) {
    if (params && Object.keys(params).length > 0) {
      const qs = Object.keys(params)
        .filter((k) => params[k] !== undefined && params[k] !== null)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join("&");
      return axios.get(qs ? `${url}?${qs}` : url);
    }
    return axios.get(url);
  }
  create(url, data, config = {}) { return axios.post(url, data, config); }
  update(url, data) { return axios.put(url, data); }
  patch(url, data)  { return axios.patch(url, data); }
  delete(url, config) { return axios.delete(url, { ...config }); }
}
